import React, { useState, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'features/common/security/hooks';
import { useReactiveVar, useSubscription } from '@apollo/client';
import { ACTIVE_DOCUMENTS_FOUND, COMPUTE_QUOTATIONS_VALIDATED, RETRIEVE_CONVENTIONS_VALIDATED } from '../subscriptions.js';
import { COMPUTE_QUOTATIONS } from '../mutations.js';
import { INSURANCE_TYPES_QUERY } from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { useDictionaryHandling, useBackdrop, useToast, useSimpleError, useParams } from 'hooks';
import { QuotationsStatus } from 'constants/defaultValues.js';
import { quotationsStatusVar } from 'features/insuranceDocument/variables.js';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import { Button } from '@totalsoft/rocket-ui';

const ActionsContainer = ({ documentId, insuranceTypeId, readOnly, dirtyInfoRef }) => {
  const [saving, setSaving] = useState(false);
  const status = useReactiveVar(quotationsStatusVar);
  const [computeDisabled, setComputeDisabled] = useState(false);
  const { documentAllowedTransitions } = useContext(InsuranceDataContext);
  const { t } = useTranslation();
  const addToast = useToast();
  const showError = useSimpleError();
  const { Backdrop, showBackdrop, hideBackdrop, setBackdropMessage } = useBackdrop();

  const handleDirtyInfoChange = () => {
    const { generalData, insuranceData, partnerData, driverData, assetData } = dirtyInfoRef.current;
    setComputeDisabled(generalData || insuranceData || partnerData || driverData || assetData);
  };

  dirtyInfoRef.current = {
    ...dirtyInfoRef.current,
    onDirtyInfoChange: handleDirtyInfoChange
  };

  const { getCodeById } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(() => getCodeById(insuranceTypeId), [getCodeById, insuranceTypeId]);

  const computing = useMemo(() => {
    if (!status) return false;
    return status !== QuotationsStatus.VALID && status !== QuotationsStatus.EXPIRED;
  }, [status]);

  const [computeQuotations] = useMutation(COMPUTE_QUOTATIONS, {
    onError: error => {
      setSaving(false);
      hideBackdrop();
      showError(error);
    }
  });

  const handleComputeQuotations = useCallback(() => {
    setSaving(true);
    setBackdropMessage(t('Offer.Edit.General.ComputeOffersWaitingMessage'));
    showBackdrop();
    computeQuotations({
      variables: {
        input: { documentId, insuranceTypeCode, recomputeQuotations: false }
      }
    });
  }, [computeQuotations, documentId, insuranceTypeCode, setBackdropMessage, showBackdrop, t]);

  useSubscription(ACTIVE_DOCUMENTS_FOUND, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      addToast(t('Offer.Edit.General.ActiveInsuranceDocumentsWarning'), 'warning', { toastId: 'activeDocumentsFoundToast' });
    }
  });

  useSubscription(COMPUTE_QUOTATIONS_VALIDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      /* Subscribe to finish global loading (check apollo/config.js for corresponding mutation(s)) */
    }
  });

  useSubscription(RETRIEVE_CONVENTIONS_VALIDATED, {
    variables: { documentId },
    skip: !documentId,
    onData: _ => {
      /* Subscribe to finish global loading (check apollo/config.js for corresponding mutation(s)) */
    }
  });

  const savedOfferDocumentStateCode = useParams('SavedOfferDocumentStateCode');

  const hasSavedOfferTransition = () => {
    if (!savedOfferDocumentStateCode) return;
    if (documentAllowedTransitions.find(x => x.code === savedOfferDocumentStateCode) != null) return true;
    else return false;
  };

  return (
    <>
      <Button
        id='InsuranceOffer_ComputeQuotations'
        color='primary'
        onClick={handleComputeQuotations}
        disabled={computeDisabled || computing || saving || readOnly || !status || !hasSavedOfferTransition}
        right={true}
      >
        {computing ? t('General.Computing') : t('General.ViewOffers')}
      </Button>
      <Backdrop />
    </>
  );
};

ActionsContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  insuranceTypeId: PropTypes.number,
  readOnly: PropTypes.bool.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired
};

export default ActionsContainer;
